import React, { useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { ArrowDownRight, ArrowRight, Check } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import PartnerForm from "@/components/PartnerForm";
import HeroIPhoneCluster from "@/components/HeroIPhoneCluster";
import RestaurantPreviewSection from "@/components/RestaurantPreviewSection";
import { useLanguage } from "@/context/LanguageContext";
import { trackScan } from "@/lib/api";
import { track, trackOnce } from "@/lib/analytics";
import { LOGOS } from "@/lib/assets";

const HERO_BG = "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1600&q=80";

export default function PartnerLanding() {
  const { t, lang } = useLanguage();

  useEffect(() => {
    // Always start at the top when landing on /partner
    window.scrollTo(0, 0);
    trackScan({ page: "partner", language: lang });
    track("page_view_partner");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reduced = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  useEffect(() => {
    const handler = () => {
      const sc = window.scrollY + window.innerHeight;
      const total = document.documentElement.scrollHeight;
      const pct = (sc / total) * 100;
      if (pct >= 25) trackOnce("psd25", "scroll_depth_25");
      if (pct >= 50) trackOnce("psd50", "scroll_depth_50");
      if (pct >= 75) trackOnce("psd75", "scroll_depth_75");
      if (pct >= 98) trackOnce("psd100", "scroll_depth_100");
    };
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const fade = (delay = 0) => ({
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
  });
  const fadeUpView = (delay = 0) => ({
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-60px" },
    transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
  });

  return (
    <div data-testid="partner-landing" className="relative">
      <Nav />

      {/* HERO */}
      <section
        className="relative pt-24 lg:pt-28 pb-8 lg:pb-16"
        data-testid="partner-hero"
      >
        <div className="max-w-[1400px] mx-auto px-5 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-6">
            <motion.div {...fade(0)} className="section-label inline-flex items-center gap-2.5" data-testid="partner-hero-label">
              <img src={LOGOS.star} alt="" className="w-6 h-6 select-none feaststar-logo" aria-hidden="true" draggable="false" />
              <span>{t("partner.hero.label")}</span>
            </motion.div>
            <h1 className="display-xl">
              <motion.span className="block" {...fade(0.08)} data-testid="partner-hero-h1-a">{t("partner.hero.h1a")}</motion.span>
              <motion.span className="block italic text-accent" {...fade(0.16)} style={{ color: "var(--color-accent)" }}>
                {t("partner.hero.h1b")}
              </motion.span>
            </h1>
            <motion.p {...fade(0.24)} className="body-lg mt-6 max-w-xl">{t("partner.hero.sub")}</motion.p>
            <motion.div {...fade(0.32)} className="mt-8 flex flex-wrap gap-3">
              <a
                href="#partner-form"
                data-testid="partner-hero-cta-primary"
                onClick={() => track("partner_hero_cta_click")}
                className="btn-olive"
              >
                {t("partner.hero.ctaPrimary")} <ArrowDownRight size={16} />
              </a>
              <a href="#partner-problem" data-testid="partner-hero-cta-secondary" className="btn-ghost">
                {t("partner.hero.ctaSecondary")} <ArrowRight size={16} />
              </a>
            </motion.div>
          </div>

          {/* iPhone cluster on partner hero */}
          <div className="lg:col-span-6 relative">
            <div className="relative w-full mx-auto" data-testid="partner-hero-phone-canvas">
              <HeroIPhoneCluster reducedMotion={reduced} />
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section id="partner-problem" className="section-pad-sm" data-testid="partner-problem">
        <div className="max-w-[720px] mx-auto px-5 lg:px-12">
          <motion.div {...fadeUpView()} className="section-label">{t("partner.problem.label")}</motion.div>
          <motion.h2 {...fadeUpView(0.05)} className="display-md">
            <span className="block">{t("partner.problem.h2a")}</span>
            <span className="block italic text-accent">{t("partner.problem.h2b")}</span>
          </motion.h2>
          <motion.p {...fadeUpView(0.1)} className="body-lg mt-8 whitespace-pre-line">{t("partner.problem.body")}</motion.p>
        </div>
      </section>

      {/* HOW */}
      <section id="partner-how" className="section-pad-sm" data-testid="partner-how">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-12">
          <div className="max-w-[820px] mb-12">
            <motion.div {...fadeUpView()} className="section-label">{t("partner.how.label")}</motion.div>
            <motion.h2 {...fadeUpView(0.05)} className="display-md">
              <span className="block">{t("partner.how.h2a")}</span>
              <span className="block italic text-accent">{t("partner.how.h2b")}</span>
            </motion.h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {t("partner.how.cards").map((c, i) => (
              <motion.div key={i} {...fadeUpView(i * 0.06)} className="card-standard">
                <div className="font-mono text-[12px] text-accent mb-3">0{i + 1}</div>
                <div className="display-sm mb-3">{c.title}</div>
                <div className="body-md">{c.body}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROFILE DEMO — real Feaststar restaurant detail view */}
      <RestaurantPreviewSection
        eyebrow={lang === "de" ? "BLICK IN DIE APP" : "INSIDE THE APP"}
        headingA={lang === "de" ? "So sehen Gäste" : "How guests"}
        headingB={lang === "de" ? "Ihren Betrieb." : "see your place."}
        description={
          lang === "de"
            ? "Vertrauen 92/100. Verifizierter Partner. Founder-Card. Transparenz-Card. Saisonale Tageskarte mit namentlich genannten Erzeugern. Qualitäts-Profil mit 37/37 belegten Kriterien. So bekommt Ihr Betrieb in Feaststar genau den Auftritt, den Sie verdienen — von der Vertrauens-Anzeige bis zur transparenten Zutaten-Beschreibung."
            : "Trust 92/100. Verified partner. Founder card. Transparency card. Seasonal menu with named producers. Quality profile with 37/37 evidenced criteria. That's how your place shows up in Feaststar — exactly the appearance you deserve, from trust badges to transparent ingredient sourcing."
        }
        testId="partner-demo"
      />

      {/* BENEFITS */}
      <section className="section-pad-sm" data-testid="partner-benefits">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-12">
          <div className="max-w-[820px] mb-12">
            <motion.div {...fadeUpView()} className="section-label">{t("partner.benefits.label")}</motion.div>
            <motion.h2 {...fadeUpView(0.05)} className="display-md">
              <span className="block">{t("partner.benefits.h2a")}</span>
              <span className="block italic text-accent">{t("partner.benefits.h2b")}</span>
            </motion.h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {t("partner.benefits.cards").map((c, i) => (
              <motion.div key={i} {...fadeUpView(i * 0.06)} className="card-standard">
                <Check size={18} className="text-accent mb-3" />
                <div className="display-sm mb-3">{c.title}</div>
                <div className="body-md">{c.body}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* STANDARDS */}
      <section className="section-pad-sm" data-testid="partner-standards">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-12">
          <div className="max-w-[760px] mb-10">
            <motion.div {...fadeUpView()} className="section-label">{t("partner.standards.label")}</motion.div>
            <motion.h2 {...fadeUpView(0.05)} className="display-md">
              <span className="block">{t("partner.standards.h2a")}</span>
              <span className="block italic text-accent">{t("partner.standards.h2b")}</span>
            </motion.h2>
            <motion.p {...fadeUpView(0.1)} className="body-lg mt-6 whitespace-pre-line">{t("partner.standards.body")}</motion.p>
          </div>
          <div className="flex flex-wrap gap-2">
            {t("partner.standards.tags").map((tg) => (
              <span key={tg} className="tag-chip">{tg}</span>
            ))}
          </div>
        </div>
      </section>

      {/* FORM */}
      <section id="partner-form" className="section-pad-sm" data-testid="partner-form-section"
        onMouseEnter={() => trackOnce("pfsv", "partner_form_section_viewed")}
      >
        <div className="max-w-[860px] mx-auto px-5 lg:px-12">
          <div className="mb-10">
            <motion.div {...fadeUpView()} className="section-label">{t("partner.form.label")}</motion.div>
            <motion.h2 {...fadeUpView(0.05)} className="display-md">
              <span className="block">{t("partner.form.h2a")}</span>
              <span className="block italic text-accent">{t("partner.form.h2b")}</span>
            </motion.h2>
            <motion.p {...fadeUpView(0.1)} className="body-lg mt-5 whitespace-pre-line">{t("partner.form.intro")}</motion.p>
          </div>
          <PartnerForm />
        </div>
      </section>

      {/* CLOSING */}
      <section className="section-pad-sm" data-testid="partner-closing">
        <div className="max-w-[1000px] mx-auto px-5 lg:px-12 text-center">
          <motion.h2 {...fadeUpView()} className="display-lg">
            <span className="block">{t("partner.closing.h1a")}</span>
            <span className="block italic text-accent">{t("partner.closing.h1b")}</span>
          </motion.h2>
          <motion.p {...fadeUpView(0.05)} className="body-lg mt-6 max-w-[640px] mx-auto">{t("partner.closing.body")}</motion.p>
          <motion.div {...fadeUpView(0.1)} className="mt-8">
            <a href="#partner-form" data-testid="partner-closing-cta" className="btn-primary">
              {t("partner.closing.cta")} <ArrowDownRight size={16} />
            </a>
          </motion.div>
        </div>
      </section>

      <Footer variant="partner" />
    </div>
  );
}
